import { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box,
  Typography, TextField, Button, Alert, CircularProgress,
  Chip, InputAdornment, Paper, Grid,
} from "@mui/material";

import CloseIcon         from "@mui/icons-material/Close";
import PaymentIcon       from "@mui/icons-material/Payment";
import TwoWheelerIcon    from "@mui/icons-material/TwoWheeler";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import NotesIcon         from "@mui/icons-material/Notes";

import api from "../service/api";

// ─── shared helpers (also imported by ExchangeSellModal / UpdateExchangePaymentModal) ──
export const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const Label = ({ children }) => (
  <Typography
    variant="caption"
    sx={{ color: "#888", fontWeight: 700, fontSize: "0.62rem", letterSpacing: 0.6, textTransform: "uppercase" }}
  >
    {children}
  </Typography>
);
const Val = ({ children, color }) => (
  <Typography variant="body2" fontWeight={700} color={color || "#111"} mt={0.2}>
    {children || "—"}
  </Typography>
);

// ─── Due summary block: totals + due payment history (payments[0].dueExchange) ──
export const DueSummary = ({ due }) => {
  if (!due) return null;
  const isPaid = due.status === "PAID";
  const list   = due.payments || [];

  return (
    <Paper elevation={0} sx={{
      p: 1.8, mb: 2, borderRadius: 2,
      border: `1px solid ${isPaid ? "#a5d6a7" : "#ffb74d"}`,
      bgcolor: isPaid ? "#f1f8e9" : "#fff8e1",
    }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="caption" fontWeight={800} letterSpacing={0.5} textTransform="uppercase"
          color={isPaid ? "#2e7d32" : "#e65100"}>
          Exchange Due
        </Typography>
        <Chip label={due.status || "—"} size="small" sx={{
          fontWeight: 800, fontSize: "0.62rem",
          bgcolor: isPaid ? "#e8f5e9" : "#fff3e0",
          color:   isPaid ? "#2e7d32" : "#e65100",
          border:  `1px solid ${isPaid ? "#a5d6a7" : "#ffb74d"}`,
        }} />
      </Box>

      <Grid container spacing={1}>
        <Grid item xs={4}><Label>Due Total</Label><Val color="#333">{money(due.totalAmount)}</Val></Grid>
        <Grid item xs={4}><Label>Paid</Label><Val color="#2e7d32">{money(due.paidAmount)}</Val></Grid>
        <Grid item xs={4}>
          <Label>Remaining</Label>
          <Val color={due.remainingAmount > 0 ? "#c62828" : "#2e7d32"}>{money(due.remainingAmount)}</Val>
        </Grid>
      </Grid>

      {list.length > 0 && (
        <Box mt={1.2} display="flex" flexDirection="column" gap={0.7}>
          <Label>Due Payments</Label>
          {list.map((p, i) => (
            <Box key={i} display="flex" alignItems="center" justifyContent="space-between"
              sx={{ bgcolor: "#fff", borderRadius: 1.5, px: 1.5, py: 0.8, border: "1px solid #e0e7ef" }}>
              <Box>
                <Typography variant="caption" fontWeight={700} color="#1565c0" display="block">
                  Due Payment {i + 1}
                </Typography>
                <Typography variant="body2" fontWeight={700} color="#2e7d32">{money(p.amount)}</Typography>
                {p.note && <Typography variant="caption" color="#555" display="block">📝 {p.note}</Typography>}
              </Box>
              <Typography variant="caption" color="#999">
                {p.date ? new Date(p.date).toLocaleDateString("en-IN") : "—"}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
};

// ─── COMPONENT: add a due payment → POST /add-exchange-due-payment/:saleId ─────
const ExchangeDuePaymentModal = ({ open, onClose, bike, onSuccess }) => {

  const [due,          setDue]          = useState(null);
  const [saleId,       setSaleId]       = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError,   setFetchError]   = useState("");

  const [form,       setForm]       = useState({ amount: "", date: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [formError,  setFormError]  = useState("");
  const [success,    setSuccess]    = useState(false);

  // ── fetch when modal opens ─────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !bike?._id) return;

    const load = async () => {
      try {
        setFetchLoading(true);
        setFetchError("");
        setDue(null);
        setSuccess(false);
        setFormError("");
        setForm({ amount: "", date: new Date().toISOString().slice(0, 10), note: "" });

        const res  = await api.get(`/specific-bike-details/${bike._id}`);
        const data = res.data?.data;

        setSaleId(
          bike.ExchangeSellId?._id ||
          bike.ExchangeSellId ||
          data?.stockDetails?.ExchangeSellId?._id ||
          null
        );

        const first = data?.exchangeSaleInfo?.exchange?.payments?.[0];
        setDue(first?.dueExchange || null);
      } catch {
        setFetchError("Failed to load due details. Please try again.");
      } finally {
        setFetchLoading(false);
      }
    };

    load();
  }, [open, bike]);

  // ── submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setFormError("");

    if (!form.amount || Number(form.amount) <= 0) { setFormError("Please enter a valid amount."); return; }
    if (!form.date)                                { setFormError("Payment date is required.");    return; }
    if (due && Number(form.amount) > due.remainingAmount) {
      setFormError(`Max payable amount is ${money(due.remainingAmount)}.`);
      return;
    }

    try {
      setSubmitting(true);
      const res = await api.post(`/add-exchange-due-payment/${saleId}`, {
        amount: Number(form.amount),
        date:   form.date,
        note:   form.note.trim(),
      });
      const d = res.data?.data;
      setDue({
        totalAmount:     d.totalAmount,
        paidAmount:      d.paidAmount,
        remainingAmount: d.remainingAmount,
        status:          d.status,
        payments:        d.payments || [],
      });
      setSuccess(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      setFormError(err?.response?.data?.message || "Payment failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => { if (!submitting) onClose(); };

  const isPaid = due?.status === "PAID";

  const titleText = success ? "Due Payment Saved!" : "Exchange Due Payment";
  const titleBg   = success
    ? "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)"
    : "linear-gradient(135deg, #bf360c 0%, #e65100 100%)";

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>

      <DialogTitle sx={{
        fontWeight: 800, background: titleBg, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        py: 1.8, px: 2.5,
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          {success ? <CheckCircleIcon sx={{ fontSize: 20 }} /> : <PaymentIcon sx={{ fontSize: 20 }} />}
          <Typography fontWeight={800} fontSize="1rem">{titleText}</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: "#fff" }} disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, bgcolor: "#f8fafc" }}>

        {fetchLoading && (
          <Box textAlign="center" py={6}>
            <CircularProgress size={32} sx={{ color: "#e65100" }} />
            <Typography variant="caption" display="block" mt={1.5} color="text.secondary">
              Loading due details…
            </Typography>
          </Box>
        )}

        {!fetchLoading && fetchError && (
          <Box>
            <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{fetchError}</Alert>
            <Button fullWidth variant="outlined" onClick={handleClose} sx={{ borderRadius: 2 }}>Close</Button>
          </Box>
        )}

        {!fetchLoading && !fetchError && (
          <>
            {/* ── Bike card ── */}
            <Paper elevation={0} sx={{
              p: 1.8, mb: 2, borderRadius: 2,
              border: "1px solid #e0e7ef",
              background: "linear-gradient(135deg, #fff8f5 0%, #fff 100%)",
            }}>
              <Box display="flex" alignItems="center" gap={1} mb={1.2}>
                <TwoWheelerIcon sx={{ color: "#e65100", fontSize: 17 }} />
                <Typography fontWeight={800} fontSize="0.78rem" color="#e65100" letterSpacing={0.3}>
                  Bike Details
                </Typography>
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={6}><Label>Model</Label><Val>{bike?.model}</Val></Grid>
                <Grid item xs={6}><Label>Reg. Number</Label><Val>{bike?.registrationNumber}</Val></Grid>
                <Grid item xs={6}><Label>Owner</Label><Val>{bike?.ownerName}</Val></Grid>
                <Grid item xs={6}><Label>Mobile</Label><Val>{bike?.mobile}</Val></Grid>
              </Grid>
            </Paper>

            {/* ── No first payment yet ── */}
            {!due && (
              <>
                <Alert severity="warning" sx={{ borderRadius: 2, mb: 2, fontSize: "0.78rem" }}>
                  No due record found. Record the first exchange payment before adding a due payment.
                </Alert>
                <Button fullWidth variant="outlined" onClick={handleClose} sx={{ borderRadius: 2 }}>Close</Button>
              </>
            )}

            {/* ── Due summary + due payment history ── */}
            {due && <DueSummary due={due} />}

            {/* ── Saved ── */}
            {due && success && (
              <Button fullWidth variant="contained" onClick={handleClose}
                sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#2e7d32", "&:hover": { bgcolor: "#1b5e20" } }}>
                Done
              </Button>
            )}

            {/* ── Already fully paid ── */}
            {due && !success && isPaid && (
              <>
                <Alert severity="info" sx={{ borderRadius: 2, mb: 2, fontSize: "0.78rem" }}>
                  This exchange is already fully paid.
                </Alert>
                <Button fullWidth variant="contained" onClick={handleClose}
                  sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#546e7a", "&:hover": { bgcolor: "#37474f" } }}>
                  Close
                </Button>
              </>
            )}

            {/* ── Form ── */}
            {due && !success && !isPaid && (
              <>
                <Box display="flex" flexDirection="column" gap={1.8}>
                  <TextField label="Amount (₹)" type="number" size="small" fullWidth
                    value={form.amount}
                    onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                    inputProps={{ min: 1, max: due.remainingAmount }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon sx={{ fontSize: 15, color: "#e65100" }} /></InputAdornment> }}
                    helperText={`Max payable: ${money(due.remainingAmount)}`}
                  />
                  <TextField label="Payment Date" type="date" size="small" fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={form.date}
                    onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon sx={{ fontSize: 14, color: "#666" }} /></InputAdornment> }}
                  />
                  <TextField label="Note (optional)" size="small" fullWidth multiline rows={2}
                    value={form.note}
                    onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start"><NotesIcon sx={{ fontSize: 15, color: "#666" }} /></InputAdornment> }}
                  />
                </Box>

                {formError && (
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2, fontSize: "0.8rem" }}>{formError}</Alert>
                )}

                <Box display="flex" gap={1.5} mt={2.5}>
                  <Button fullWidth variant="outlined" onClick={handleClose} disabled={submitting}
                    sx={{ borderRadius: 2, fontWeight: 700 }}>
                    Cancel
                  </Button>
                  <Button fullWidth variant="contained" onClick={handleSubmit} disabled={submitting}
                    sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#e65100", "&:hover": { bgcolor: "#bf360c" } }}>
                    {submitting
                      ? <Box display="flex" alignItems="center" gap={1}><CircularProgress size={14} sx={{ color: "#fff" }} />Saving…</Box>
                      : "✅ Save Due Payment"}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExchangeDuePaymentModal;